import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    X,
    ChevronLeft,
    ChevronRight,
    Upload,
    Send,
    Loader2,
    Check,
    User as UserIcon,
    Camera,
    ImageOff,
} from "lucide-react";
import { api, resolveMediaUrl, formatApiErrorDetail } from "@/lib/api";
import { SkeletonGrid } from "@/components/Skeleton";
import { useLang } from "@/i18n/LangContext";

const MAX_FILE_SIZE = 12 * 1024 * 1024; // 12 MB - enforced client-side

/**
 * Public Community Screenshots section
 * ─────────────────────────────────────
 * • Loads /api/community-screenshots (approved only, newest first)
 * • Auto-renders section ONLY when there is at least one approved screenshot
 *   OR the user hits the "Prześlij swój" button (the CTA is always visible)
 * • Users can upload their own screenshot which lands in the admin moderation queue
 */
export default function CommunityScreenshots() {
    const { t } = useLang();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null); // lightbox index
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/community-screenshots?limit=30");
                if (!cancelled) setItems(data || []);
            } catch {
                if (!cancelled) setItems([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const close = () => setActive(null);
    const next = () =>
        setActive((i) => (i === null ? null : (i + 1) % items.length));
    const prev = () =>
        setActive((i) =>
            i === null ? null : (i - 1 + items.length) % items.length
        );

    return (
        <section
            id="community"
            data-testid="community-section"
            className="relative py-24 md:py-32"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                    <div>
                        <div className="label-eyebrow mb-4">
                            {t("07 - Społeczność")}
                        </div>
                        <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl tracking-tighter text-white">
                            {t("Kadry od graczy")}
                        </h2>
                    </div>
                    <div className="flex flex-col md:items-end gap-3 max-w-md">
                        <p className="text-base text-[color:var(--text-secondary)]">
                            {t("Podziel się swoim ujęciem z Rudy Śląskiej - po zatwierdzeniu pojawi się tutaj.")}
                        </p>
                        <button
                            onClick={() => setShowForm(true)}
                            data-testid="community-open-form"
                            className="btn-primary"
                        >
                            <Camera size={14} />
                            {t("Prześlij swój screenshot")}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <SkeletonGrid
                        count={6}
                        className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                    />
                ) : items.length === 0 ? (
                    <div
                        data-testid="community-empty"
                        className="panel-yellow p-16 text-center text-[color:var(--text-secondary)]"
                    >
                        <ImageOff size={28} className="mx-auto mb-3 text-[color:var(--primary)]" />
                        {t("Bądź pierwszy - kliknij powyżej i podeślij swój screen.")}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {items.map((it, i) => (
                            <motion.button
                                key={it.id}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.15 }}
                                transition={{ delay: i * 0.04 }}
                                onClick={() => setActive(i)}
                                className="group relative aspect-video rounded-lg overflow-hidden border border-[color:var(--border-strong)] hover:border-[color:var(--primary)]/50 bg-[color:var(--bg-surface)] transition text-left"
                                data-testid={`community-item-${i}`}
                            >
                                <img
                                    src={resolveMediaUrl(it.image_url)}
                                    alt={it.description || `Screenshot by ${it.author_name}`}
                                    loading="lazy"
                                    decoding="async"
                                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                                />
                                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
                                    <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--primary)] flex items-center gap-1">
                                        <UserIcon size={11} />
                                        {it.author_name || t("Anonim")}
                                    </div>
                                    {it.description && (
                                        <div className="mt-1 text-xs text-white/85 line-clamp-2">
                                            {it.description}
                                        </div>
                                    )}
                                </div>
                            </motion.button>
                        ))}
                    </div>
                )}
            </div>

            {/* Lightbox */}
            <AnimatePresence>
                {active !== null && items[active] && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
                        onClick={close}
                    >
                        <button
                            onClick={close}
                            className="absolute top-5 right-5 w-10 h-10 rounded-md flex items-center justify-center bg-white/10 text-white hover:bg-white/20"
                            data-testid="community-lightbox-close"
                        >
                            <X size={18} />
                        </button>
                        {items.length > 1 && (
                            <>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        prev();
                                    }}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                                >
                                    <ChevronLeft />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        next();
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                                >
                                    <ChevronRight />
                                </button>
                            </>
                        )}
                        <div
                            className="max-w-5xl w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={resolveMediaUrl(items[active].image_url)}
                                alt=""
                                className="w-full max-h-[80vh] object-contain rounded-md"
                            />
                            <div className="mt-3 text-center">
                                <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--primary)]">
                                    {items[active].author_name || t("Anonim")}
                                </div>
                                {items[active].description && (
                                    <div className="mt-1 text-sm text-white/85 max-w-2xl mx-auto">
                                        {items[active].description}
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Submit form modal */}
            <AnimatePresence>
                {showForm && (
                    <SubmitModal
                        onClose={() => setShowForm(false)}
                        onSuccess={() => setShowForm(false)}
                    />
                )}
            </AnimatePresence>
        </section>
    );
}

/** Modal with drag-and-drop file input, author name, description, email. */
function SubmitModal({ onClose, onSuccess }) {
    const { t } = useLang();
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState("");
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [authorName, setAuthorName] = useState("");
    const [description, setDescription] = useState("");
    const [email, setEmail] = useState("");

    const pickFile = (f) => {
        setError("");
        if (!f) return;
        if (!f.type.startsWith("image/")) {
            setError(t("Dozwolone są tylko obrazy."));
            return;
        }
        if (f.size > MAX_FILE_SIZE) {
            setError(t("Plik jest za duży (max 12 MB)."));
            return;
        }
        setFile(f);
        setPreview(URL.createObjectURL(f));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!file) {
            setError(t("Wybierz plik z obrazem."));
            return;
        }
        setSubmitting(true);
        setError("");
        try {
            setUploading(true);
            const form = new FormData();
            form.append("file", file);
            const { data: uploaded } = await api.post(
                "/community-screenshots/upload",
                form,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            setUploading(false);
            await api.post("/community-screenshots/submit", {
                image_url: uploaded.url,
                author_name: authorName.trim(),
                description: description.trim(),
                email: email.trim(),
            });
            setSuccess(true);
            setTimeout(() => {
                onSuccess?.();
            }, 1600);
        } catch (e2) {
            setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setSubmitting(false);
            setUploading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="community-submit-modal"
        >
            <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 10 }}
                className="max-w-lg w-full bg-[color:var(--bg-surface)] border border-white/10 rounded-lg overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
                    <Camera size={16} className="text-[color:var(--primary)]" />
                    <div className="flex-1">
                        <div className="text-white text-sm font-display uppercase tracking-wider">
                            {t("Prześlij screenshot")}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">
                            {t("Publikacja po zatwierdzeniu przez admina")}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="btn-ghost text-xs"
                        data-testid="community-close-form"
                    >
                        <X size={12} />
                        {t("Zamknij")}
                    </button>
                </div>

                {success ? (
                    <div className="p-8 text-center">
                        <div className="w-14 h-14 rounded-full bg-emerald-500/15 text-emerald-400 mx-auto flex items-center justify-center mb-3">
                            <Check size={28} />
                        </div>
                        <div className="text-white text-lg font-display uppercase tracking-wide mb-1">
                            {t("Dzięki!")}
                        </div>
                        <div className="text-sm text-[color:var(--text-secondary)]">
                            {t("Twój screen czeka na moderację.")}
                        </div>
                    </div>
                ) : (
                    <form className="p-5 space-y-4" onSubmit={submit}>
                        {/* File picker */}
                        <label
                            className={`block relative aspect-video rounded-md border-2 border-dashed cursor-pointer overflow-hidden transition ${
                                preview
                                    ? "border-[color:var(--primary)]/60"
                                    : "border-white/15 hover:border-white/30 bg-black/30"
                            }`}
                        >
                            <input
                                type="file"
                                accept="image/*"
                                className="sr-only"
                                onChange={(e) => pickFile(e.target.files?.[0])}
                                data-testid="community-file-input"
                            />
                            {preview ? (
                                <img
                                    src={preview}
                                    alt=""
                                    className="absolute inset-0 w-full h-full object-contain bg-black"
                                />
                            ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-[color:var(--text-secondary)]">
                                    <Upload size={26} className="mb-2 text-[color:var(--primary)]" />
                                    <div className="text-sm">
                                        {t("Kliknij, żeby wybrać obraz")}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] mt-1">
                                        {t("JPG / PNG / WebP · max 12 MB")}
                                    </div>
                                </div>
                            )}
                        </label>

                        <label className="block">
                            <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                                {t("Nick / imię (opcj.)")}
                            </span>
                            <input
                                className="input"
                                value={authorName}
                                onChange={(e) => setAuthorName(e.target.value)}
                                placeholder={t("np. TruckerJoe")}
                                maxLength={60}
                                data-testid="community-author"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                                {t("Opis (opcj.)")}
                            </span>
                            <textarea
                                className="input"
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder={t("Kilka słów o Twoim ujęciu…")}
                                maxLength={400}
                                data-testid="community-description"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                                {t("Email (opcj., prywatny)")}
                            </span>
                            <input
                                className="input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="-"
                                maxLength={120}
                                data-testid="community-email"
                            />
                        </label>

                        {error && (
                            <div className="text-xs text-red-400" data-testid="community-error">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting || !file}
                            className="btn-primary w-full disabled:opacity-60"
                            data-testid="community-submit"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={14} className="animate-spin" />
                                    {uploading ? t("Wysyłam obraz…") : t("Zgłaszam…")}
                                </>
                            ) : (
                                <>
                                    <Send size={13} />
                                    {t("Wyślij do moderacji")}
                                </>
                            )}
                        </button>
                    </form>
                )}
            </motion.div>
        </motion.div>
    );
}
